import { useState } from 'react'
import type { Crew } from '@/types'

/* ── Inline SVG icons ── */
const ChevronIcon = () => (
  <svg style={{ width: 16, height: 16 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const DeleteIcon = () => (
  <svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

// Mock crew data
const MOCK_CREWS: Crew[] = [
  {
    id: '1',
    tenantId: 't1',
    name: 'Paving Crew A',
    description: 'Primary asphalt paving crew',
    members: [
      { id: 'm1', crewId: '1', pricingItemId: 'lab1', quantity: 1, role: 'Foreman', rate: 140, subtotal: 140 },
      { id: 'm2', crewId: '1', pricingItemId: 'lab2', quantity: 1, role: 'Operator', rate: 120, subtotal: 120 },
      { id: 'm3', crewId: '1', pricingItemId: 'lab3', quantity: 3, role: 'Laborer', rate: 85, subtotal: 255 },
    ],
    totalHourlyRate: 515,
    createdAt: '2025-01-20',
  },
  {
    id: '2',
    tenantId: 't1',
    name: 'Striping Crew',
    description: 'Road marking and striping crew',
    members: [
      { id: 'm4', crewId: '2', pricingItemId: 'lab1', quantity: 1, role: 'Foreman', rate: 140, subtotal: 140 },
      { id: 'm5', crewId: '2', pricingItemId: 'lab4', quantity: 2, role: 'Flagger', rate: 65, subtotal: 130 },
      { id: 'm6', crewId: '2', pricingItemId: 'lab3', quantity: 2, role: 'Laborer', rate: 85, subtotal: 170 },
    ],
    totalHourlyRate: 440,
    createdAt: '2025-01-21',
  },
]

export function CrewsPage() {
  const [crews, setCrews] = useState<Crew[]>(MOCK_CREWS)
  const [expandedCrewId, setExpandedCrewId] = useState<string | null>(null)

  const handleToggleExpand = (crewId: string) => {
    setExpandedCrewId(expandedCrewId === crewId ? null : crewId)
  }

  const handleRemoveMember = (crewId: string, memberId: string) => {
    setCrews(
      crews.map((crew) => {
        if (crew.id === crewId) {
          const updatedMembers = crew.members.filter((m) => m.id !== memberId)
          const totalHourlyRate = updatedMembers.reduce(
            (sum, m) => sum + m.rate * m.quantity,
            0
          )
          return { ...crew, members: updatedMembers, totalHourlyRate }
        }
        return crew
      })
    )
  }

  const handleDeleteCrew = (crewId: string) => {
    setCrews(crews.filter((c) => c.id !== crewId))
  }

  return (
    <div>
      {/* Page Header */}
      <div className="est-list-head">
        <h1 className="est-list-title">Crews</h1>
        <button className="btn" onClick={() => {}}>+ Create Crew</button>
      </div>

      {/* Tabs */}
      <div className="est-list-tabs">
        <button className="est-list-tab active">All Crews</button>
      </div>

      {/* Crews as expandable rows inside a card */}
      <div className="card est-list-card">
        {crews.length === 0 ? (
          <div className="est-list-empty">
            <p className="est-list-empty-text">No crews created yet</p>
            <p className="est-list-empty-sub">Create your first crew to group labor roles for quick estimate entry</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <th>Crew Name</th>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Members</th>
                <th style={{ textAlign: 'right' }}>Hourly Rate</th>
                <th style={{ width: 80, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {crews.map((crew) => {
                const isExpanded = expandedCrewId === crew.id
                return (
                  <>
                    {/* Crew row */}
                    <tr
                      key={crew.id}
                      className="est-list-row"
                      onClick={() => handleToggleExpand(crew.id)}
                    >
                      <td style={{ padding: '14px 4px 14px 16px' }}>
                        <span className={`pricing-crew-chevron${isExpanded ? ' open' : ''}`}>
                          <ChevronIcon />
                        </span>
                      </td>
                      <td style={{ fontWeight: 500, color: 'var(--s900)' }}>{crew.name}</td>
                      <td style={{ color: 'var(--s500)', fontSize: 13 }}>{crew.description || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {crew.members.length}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>
                        ${crew.totalHourlyRate.toFixed(2)}/hr
                      </td>
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          className="tbl-action-btn tbl-action-btn--danger"
                          title="Delete Crew"
                          onClick={() => handleDeleteCrew(crew.id)}
                        >
                          <DeleteIcon />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded member rows */}
                    {isExpanded && crew.members.map((member) => (
                      <tr key={member.id} className="crew-member-row">
                        <td />
                        <td style={{ paddingLeft: 32, color: 'var(--s700)' }}>{member.role || 'Crew Member'}</td>
                        <td />
                        <td style={{ textAlign: 'center' }}>{member.quantity}</td>
                        <td style={{ textAlign: 'right' }}>${member.rate.toFixed(2)}/hr</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="tbl-action-btn tbl-action-btn--danger"
                            title="Remove Member"
                            onClick={() => handleRemoveMember(crew.id, member.id)}
                          >
                            <DeleteIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
